import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { renderTemplate } from "@/components/Conversation";

const VARS = ["name", "full_name", "ticket_id", "product", "query", "email", "phone", "batch", "agent"];
const SAMPLE = { name: "Aditi", full_name: "Aditi Sharma", ticket_id: "VS-0042", product: "EAG-AI Workshop", query: "I can't find my recordings", email: "aditi@example.com", phone: "98765 43210", batch: "C10-B1", agent: "Priya Sharma" };
const EMPTY = { title: "", body: "" };

export function ReplyTemplates({ kind = "reply" }) {
  const qc = useQueryClient();
  const noun = kind === "closing" ? "closing summary" : "saved reply";
  const { data: templates = [] } = useQuery({ queryKey: ["templates", kind], queryFn: () => api.get("/templates", { params: { kind } }).then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["templates"] });

  const save = useMutation({
    mutationFn: () => (editing ? api.put(`/templates/${editing}`, { ...form, kind }) : api.post("/templates", { ...form, kind })).then((r) => r.data),
    onSuccess: () => {
      toast.success(editing ? "Template updated" : "Template created");
      setEditing(null);
      setForm(EMPTY);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/templates/${id}`),
    onSuccess: () => {
      toast.success("Template deleted");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const startEdit = (t) => {
    setEditing(t.id);
    setForm({ title: t.title, body: t.body });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_420px]" data-testid={kind === "closing" ? "closing-templates" : "reply-templates"}>
      <ul className="space-y-3">
        {kind === "closing" && <li className="rounded-xl border-2 border-black bg-[#FEF9C3] p-4 text-sm">Executives must write a closing summary to resolve a ticket. These templates appear in their picker; they can edit before sending. The summary is emailed to the customer with the rating link.</li>}
        {templates.length === 0 && <li className="rounded-xl border-2 border-dashed border-zinc-400 bg-white p-6 text-center text-sm text-zinc-600" data-testid="templates-empty">No {noun} templates yet.</li>}
        {templates.map((t) => (
          <li key={t.id} className={`brut-card p-4 ${editing === t.id ? "bg-[#FEF9C3]" : ""}`} data-testid={`template-row-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="flex items-center gap-2 font-display text-lg font-bold"><FileText className="h-4 w-4" /> {t.title}</p>
              <div className="flex gap-1">
                <button className="btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button className="btn-ghost !px-2.5 !py-1.5 !text-xs hover:!bg-[#FEF2F2]" onClick={() => remove.mutate(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-700">{t.body}</p>
          </li>
        ))}
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="brut-card brut-shadow h-fit space-y-4 p-5"
        data-testid="template-form"
      >
        <h3 className="flex items-center justify-between font-display text-xl font-bold">
          <span className="flex items-center gap-2">{editing ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />} {editing ? `Edit ${noun}` : `New ${noun}`}</span>
          {editing && (
            <button type="button" className="text-xs font-semibold text-zinc-500 hover:text-black" onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="template-cancel-edit">
              <X className="inline h-3.5 w-3.5" /> cancel
            </button>
          )}
        </h3>
        <div>
          <label className="field-label">Title</label>
          <input className="field-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required minLength={2} placeholder="e.g. Recording link" data-testid="template-title-input" />
        </div>
        <div>
          <label className="field-label">{kind === "closing" ? "Summary text" : "Reply text"}</label>
          <textarea className="field-input min-h-[180px]" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required minLength={5} placeholder="Hi {{name}}, …" data-testid="template-body-input" />
          <p className="mt-1.5 text-xs text-zinc-500">
            Placeholders: {VARS.map((v) => (
              <button type="button" key={v} className="mr-1 rounded bg-zinc-100 px-1.5 py-0.5 font-mono hover:bg-[#FEF9C3]" onClick={() => setForm({ ...form, body: `${form.body}{{${v}}}` })} data-testid={`template-var-${v}`}>
                {`{{${v}}}`}
              </button>
            ))}
          </p>
        </div>
        {form.body && (
          <div className="rounded-xl border-2 border-dashed border-zinc-400 bg-zinc-50 p-3 text-sm" data-testid="template-preview">
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-zinc-500">Preview with sample ticket</p>
            <p className="whitespace-pre-wrap">{renderTemplate(form.body, SAMPLE)}</p>
          </div>
        )}
        <button type="submit" className="btn-primary w-full" disabled={save.isPending} data-testid="template-save-button">
          {save.isPending ? "Saving…" : editing ? "Update template" : "Create template"}
        </button>
      </form>
    </div>
  );
}
